import { useEffect, useState } from "react";
import { Trash2, Download, EyeOff, Eye, CloudDownload } from "lucide-react";
import { toast } from "sonner";
import { adminApi, adminUrl, money, fmtDate, errMsg } from "../lib/api";
import { Card, Title, Badge } from "./kit";
import { Stars } from "../components/Bits";

export function Newsletter() {
  const [list, setList] = useState([]);
  const [st, setSt] = useState(null);
  const [q, setQ] = useState("");
  const load = () => {
    adminApi.get("/newsletter").then((r) => setList(r.data));
    adminApi.get("/newsletter/stats").then((r) => setSt(r.data));
  };
  useEffect(() => {
    load();
  }, []);
  useEffect(() => {
    if (st?.import?.status !== "running") return undefined;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [st?.import?.status]);
  const startImport = async () => {
    try {
      await adminApi.post("/newsletter/import-mailerlite");
      toast.success("Import z MailerLite spuštěn");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const shown = list.filter((n) => !q || n.email.includes(q.toLowerCase())).slice(0, 300);
  return (
    <div data-testid="admin-newsletter">
      <Title right={<div className="flex gap-2"><button onClick={startImport} disabled={st?.import?.status === "running"} className="btn-ghost" data-testid="newsletter-import-btn"><CloudDownload className="w-4 h-4" /> Import z MailerLite</button><a href={adminUrl("/newsletter/export.csv")} className="btn-ghost" data-testid="newsletter-export-btn"><Download className="w-4 h-4" /> Export CSV</a></div>}>Odběratelé</Title>
      <div className="flex flex-wrap gap-3 mb-4 text-sm">
        <Badge tone="teal">aktivní: {st?.active ?? "…"}</Badge><Badge tone="gray">odhlášení: {st?.unsubscribed ?? "…"}</Badge>
        {st?.import?.status && <span className="text-[var(--muted)]" data-testid="newsletter-import-status">Import z MailerLite: {st.import.status === "running" ? `probíhá… (${st.import.imported})` : st.import.message}</span>}
      </div>
      <p className="text-sm text-[var(--muted)] mb-4">Import načte všechny aktivní kontakty z MailerLite (API klíč v API & nastavení) a přidá je mezi odběratele. Existující i odhlášené kontakty nepřepíše. Newslettery pak posíláte v záložce Kampaně.</p>
      <input className="field max-w-sm mb-3" placeholder="Hledat e-mail…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="newsletter-search" />
      <div className="space-y-2">
        {shown.map((n) => (
          <Card key={n.id} className="!p-3 flex items-center gap-3 text-sm" data-testid={`subscriber-${n.email}`}>
            <span className={`font-bold flex-1 ${n.unsubscribed ? "line-through text-[var(--muted)]" : ""}`}>{n.email}</span>{n.unsubscribed && <Badge tone="gray">odhlášen</Badge>}<Badge tone="teal">{n.source}</Badge><span className="text-[var(--muted)]">{fmtDate(n.created_at)}</span>
            <button onClick={async () => { if (window.confirm("Odebrat odběratele?")) { await adminApi.delete(`/newsletter/${n.id}`); load(); } }} className="w-8 h-8 grid place-items-center rounded-full hover:bg-[var(--blush)]" data-testid={`subscriber-delete-${n.id}`}><Trash2 className="w-4 h-4" /></button>
          </Card>
        ))}
      </div>
    </div>
  );
}

export function Reviews() {
  const [list, setList] = useState([]);
  const load = () => adminApi.get("/reviews").then((r) => setList(r.data));
  useEffect(() => {
    load();
  }, []);
  return (
    <div data-testid="admin-reviews">
      <Title>Hodnocení materiálů</Title>
      <div className="space-y-2">
        {list.length === 0 && <Card className="text-sm text-[var(--muted)]">Zatím žádná hodnocení. Hodnotit mohou jen zákazníci, kteří materiál koupili.</Card>}
        {list.map((r) => (
          <Card key={r.id} className="!p-4 flex flex-wrap items-start gap-4" data-testid={`admin-review-${r.id}`}>
            <div className="flex-1 min-w-[200px]"><p className="font-bold">{r.product_name}</p><Stars value={r.rating} /><p className="text-sm mt-1">{r.text}</p><p className="text-xs text-[var(--muted)]">{r.name} · {fmtDate(r.created_at)}</p></div>
            {!r.approved && <Badge tone="gray">Skryto</Badge>}
            <button onClick={async () => { await adminApi.put(`/reviews/${r.id}`, { approved: !r.approved }); load(); }} className="w-9 h-9 grid place-items-center rounded-full hover:bg-[var(--blush)]" title="Skrýt / zobrazit">{r.approved ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
            <button onClick={async () => { if (window.confirm("Smazat hodnocení?")) { await adminApi.delete(`/reviews/${r.id}`); load(); } }} className="w-9 h-9 grid place-items-center rounded-full hover:bg-[var(--blush)]"><Trash2 className="w-4 h-4" /></button>
          </Card>
        ))}
      </div>
    </div>
  );
}

export function Customers() {
  const [users, setUsers] = useState([]);
  const [msgs, setMsgs] = useState([]);
  useEffect(() => {
    adminApi.get("/customers").then((r) => setUsers(r.data));
    adminApi.get("/contact-messages").then((r) => setMsgs(r.data));
  }, []);
  return (
    <div className="grid xl:grid-cols-2 gap-6" data-testid="admin-customers">
      <div>
        <Title>Zákazníci ({users.length})</Title>
        <div className="space-y-2">
          {users.map((u) => (
            <Card key={u.id} className="!p-3 flex items-center gap-3 text-sm"><div className="flex-1"><p className="font-bold">{u.name || "—"}</p><p className="text-[var(--muted)]">{u.email} · od {fmtDate(u.created_at)}</p></div><span>{u.orders} obj.</span><span className="font-bold">{money(u.spent)}</span></Card>
          ))}
        </div>
      </div>
      <div>
        <Title>Zprávy z kontaktního formuláře</Title>
        <div className="space-y-2">
          {msgs.length === 0 && <Card className="text-sm text-[var(--muted)]">Žádné zprávy.</Card>}
          {msgs.map((m) => (
            <Card key={m.id} className="!p-4 text-sm"><p className="font-bold">{m.name} · <a href={`mailto:${m.email}`} className="underline">{m.email}</a></p><p className="whitespace-pre-line mt-1">{m.message}</p><p className="text-xs text-[var(--muted)] mt-1">{fmtDate(m.created_at)}</p></Card>
          ))}
        </div>
      </div>
    </div>
  );
}
